<template>
  <tr>
    <td id="rating">
      <div class="mr-3 star-rating">
        <star-rating
          class="star-rating-vue"
          border-color="#DDD"
          :read-only="pipeline.is_deleted"
          active-color="#f05c00"
          :animate="true"
          :rating="pipeline.rating"
          @rating-selected="setCurrentSelectedRating($event, pipeline['pipeline_id'])"
          :id="pipeline['pipeline_id']"
          :border-width="2"
          :show-rating="false"
          :rounded-corners="true"
          :star-points="starPoints"
          :star-size="20"
        ></star-rating>
        <span
          class="reset-stars"
          @click="setCurrentSelectedRating((rating = 0), pipeline['pipeline_id'])"
        >
          <i class="fas fa-times-circle"></i>
        </span>
      </div>
    </td>
    <td id="pipeline-title" nowrap>
      <popper
        trigger="hover"
        :options="{
          placement: 'top',
          modifiers: { offset: { offset: '0,10px' } },
        }"
      >
        <div class="popper">
          {{ pipeline.job }}
        </div>
        <div slot="reference" class="truncate-item">
          <del v-if="pipeline.is_deleted"
            ><a>{{ pipeline.job }}</a></del
          >
          <router-link
            v-else
            style="text-decoration: none"
            :to="{ name: 'job_uuid', params: { job_uuid: pipeline['job_id'] } }"
            >{{ pipeline.job }}</router-link
          >
        </div>
      </popper>
    </td>
    <td nowrap>
      <del v-if="pipeline.is_deleted" class="view-control mr-2">
        {{ pipeline.employer }}
      </del>
      <span class="view-control mr-2" v-else>
        <router-link :to="{ name: 'employer_id', params: { employer_id: pipeline.employer_id } }">
          {{ pipeline.employer }}</router-link
        >
      </span>
    </td>
    <td id="location" nowrap class="location-color">
      {{ pipeline.hasOwnProperty('location') ? pipeline.location.city : '' }},
      {{ pipeline.hasOwnProperty('location') ? pipeline.location.country : '' }}
    </td>
    <td>
      <popper
        v-if="
          typeof pipeline.devskiller_data === 'object' &&
            pipeline.devskiller_data !== null &&
            (pipeline.devskiller_data.status === 'Shared' ||
              pipeline.devskiller_data.status === 'TRANSFERED_SCORE')
        "
        trigger="hover"
        :options="{
          placement: 'top',
          modifiers: { offset: { offset: '0,10px' } },
        }"
      >
        <div class="popper">
          Programming test
        </div>
        <div slot="reference" class="truncate-item">
          {{ `${pipeline.devskiller_data.score}%` }}
        </div>
      </popper>
    </td>
    <td id="pipeline-documents">
      <div class="d-flex">
        <div v-for="doc in pipelineDocuments" :key="doc.id">
          <popper
            trigger="hover"
            :options="{
              placement: 'top',
              modifiers: { offset: { offset: '0,10px' } },
            }"
          >
            <div class="popper">{{ doc.name }}</div>
            <a slot="reference">
              <div>
                <i
                  @click="openDocPreview(doc.id, doc.name)"
                  :class="[`mr-3 fa`, doc.icon]"
                  :style="
                    `cursor: pointer; color: ${
                      getDocumentProperties(doc.name).color
                    }; font-size: 22px`
                  "
                ></i>
              </div>
            </a>
          </popper>
        </div>
      </div>
    </td>
    <td id="candidate-pipeline-source" nowrap>
      <popper
        trigger="hover"
        :options="{
          placement: 'top',
          modifiers: { offset: { offset: '-60,10px' } },
        }"
      >
        <div class="popper">{{ currentSource.description }}</div>
        <div slot="reference">{{ currentSource.source_name }}</div>
      </popper>
    </td>

    <td id="job-pipelines-last-updated" style="overflow: hidden" nowrap>
      <popper
        trigger="hover"
        :options="{
          placement: 'top',
          modifiers: { offset: { offset: '-60,10px' } },
        }"
      >
        <div class="popper">
          {{
            currentUpdatedAt
              | formatDate({
                format: getUserProfile.format_date,
                timezone: getUserProfile.timezone,
                hasHourAndMinutes: true,
              })
          }}
        </div>
        <div slot="reference">
          {{
            currentUpdatedAt
              | formatDate({
                format: getUserProfile.format_date,
                timezone: getUserProfile.timezone,
                hasHourAndMinutes: false,
              })
          }}
        </div>
      </popper>
    </td>

    <td id="candidate-qualification" nowrap>
      <div class="d-flex align-items-center">
        <div
          :class="[
            getQualificationColorClassByTitle(currentQualification),
            'flex-shrink-0',
            'stage-size',
            'rounded-circle',
            'mr-2',
          ]"
        ></div>
        <span :class="[currentQualification !== 'N/A' ? 'text-nowrap' : 'mr-0', 'stage-text']"
          >{{ currentQualification }}
        </span>
      </div>
    </td>

    <td id="candidate-pipeline-stage">
      <div class="d-flex align-items-center">
        <div
          :class="[
            getStageColorClassByTitle(currentStage),
            'flex-shrink-0',
            'stage-size',
            'rounded-circle',
            'mr-2',
          ]"
        ></div>
        <span :class="[currentStage !== 'N/A' ? 'text-nowrap' : 'mr-0', 'stage-text']"
          >{{ currentStage }}
        </span>
      </div>
    </td>
    <td style="text-align: center" id="seen">
      <popper
        trigger="hover"
        :options="{
          placement: 'top',
          modifiers: { offset: { offset: '-60,10px' } },
        }"
      >
        <div v-if="pipeline.seen_by" class="popper">
          {{ pipeline.seen_by }}
          {{
            pipeline.seen_date
              | formatDate({
                format: getUserProfile.format_date,
                timezone: getUserProfile.timezone,
                hasHourAndMinutes: false,
              })
          }}
        </div>
        <div v-else class="popper">
          Not reviewed
        </div>
        <div slot="reference">
          <svgHelper name="seen" v-if="pipeline.seen_date" />
          <svgHelper name="notSeen" v-else />
        </div>
      </popper>
    </td>
    <td id="actions">
      <div class="icon-wrapper d-flex">
        <button
          @click="setDefaultDocument"
          :data-bs-target="`#editCandidatePipeline-${pipeline.pipeline_id}`"
          data-bs-toggle="modal"
          class="btn btn-link"
        >
          <svgHelper class="edit" name="edit" />
        </button>
        <button
          :data-bs-target="`#submitCandidateModal-${pipeline.pipeline_id}`"
          data-bs-toggle="modal"
          class="btn btn-link"
          :disabled="isDisabled"
        >
          <svgHelper class="submit" name="submitPipeline" />
        </button>

        <button
          :data-bs-target="`#candidatePipelineDeleteModal-${pipeline.pipeline_id}`"
          class="btn btn-link"
          data-bs-toggle="modal"
        >
          <svgHelper name="delete" />
        </button>
        <popper
          trigger="hover"
          :options="{
            placement: 'left',
            modifiers: { offset: { offset: '0,10px' } },
          }"
        >
          <div class="popper">
            <span v-if="pipeline.devskiller_data.length === 0">Send programming test</span>
            <span
              v-if="
                pipeline.devskiller_data.status === 'Sent' ||
                  pipeline.devskiller_data.status === 'Expired' ||
                  pipeline.devskiller_data.status === 'Declined'
              "
              >Programming test was sent on
              {{
                pipeline.devskiller_data.created_date
                  | formatDate({
                    format: getUserProfile.format_date,
                    timezone: getUserProfile.timezone,
                    hasHourAndMinutes: false,
                  })
              }}</span
            >
            <span
              v-if="
                pipeline.devskiller_data.status === 'Completed' ||
                  pipeline.devskiller_data.status === 'Shared'
              "
              >Programming test was completed on
              {{
                pipeline.devskiller_data.completed_date
                  | formatDate({
                    format: getUserProfile.format_date,
                    timezone: getUserProfile.timezone,
                    hasHourAndMinutes: false,
                  })
              }}</span
            >
          </div>
          <div slot="reference">
            <button
              data-bs-toggle="modal"
              :data-bs-target="`#dev-${pipeline.pipeline_id}`"
              type="button"
              class="btn btn-link send-test"
            >
              <svgHelper
                :style="pipeline.devskiller_data.status ? 'fill:#f05a00' : ''"
                name="test"
              ></svgHelper>
            </button>
          </div>
        </popper>

        <popper
          v-if="getQualificationMixin(this.pipeline.qualification).value === 'submitted'"
          trigger="hover"
          :options="{
            placement: 'left',
            modifiers: { offset: { offset: '0,10px' } },
          }"
        >
          <div class="popper">
            {{ checkInterviewStatus() }}
          </div>
          <div slot="reference">
            <button
              data-bs-toggle="modal"
              :data-bs-target="
                pipeline.interview.length === 0
                  ? `#book-${pipeline.pipeline_id}`
                  : `#confirm-${pipeline.pipeline_id}`
              "
              type="button"
              class="btn btn-link send-test"
            >
              <svgHelper
                :name="pipeline.interview.length === 0 ? 'interviewInActive' : 'interviewActive'"
              ></svgHelper>
            </button>
          </div>
        </popper>
        <button
          v-else
          disabled="true"
          style="opacity: 0.4;"
          data-bs-toggle="modal"
          type="button"
          class="btn btn-link send-test"
        >
          <svgHelper name="interviewInActive"></svgHelper>
        </button>
      </div>
    </td>

    <!-- Edit Modal -->
    <div
      :id="`editCandidatePipeline-${pipeline.pipeline_id}`"
      class="modal fade edit-modal"
      tabindex="-1"
      aria-labelledby="editCandidatePipeline"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editCandidatePipelineLabel">Edit Pipeline</h5>
            <button
              ref="closeEditModal"
              @click="onCancelEditCandidatePipeline"
              type="button"
              class="close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group m-label-left">
              <input
                type="text"
                class="form-control mb-3"
                readonly
                disabled
                :value="`${pipeline.job}, ${pipeline.location.city}`"
              />
              <label for="stage">Position</label>
            </div>

            <div class="form-group m-label-left">
              <input
                type="text"
                class="form-control mb-3"
                readonly
                disabled
                :value="`${pipeline.employer}`"
              />
              <label for="stage">Employer</label>
            </div>

            <div
              v-if="
                getQualificationMixin(pipeline.qualification).value === 'submitted' ||
                  currentQualification === 'Submitted'
              "
              class="form-group m-label-left"
            >
              <select
                id="select-stage"
                v-model="selectedStage"
                @change="handleSelectedStage"
                class="form-control"
              >
                <option value="not_contacted">Not contacted</option>
                <option value="contacted">Contacted</option>
                <option value="interviewing">Interviewing</option>
                <option value="offered_employment">Offered employment</option>
                <option value="employed">Employed</option>
                <option value="no_longer_interested">No longer interested</option>
                <option value="not_in_consideration">Not in consideration</option>
              </select>
              <label for="stage">Stage</label>
            </div>

            <div class="form-group m-label-left mb-4" v-else>
              <select
                id="candidate-pipeline-item-qualification"
                class="form-control"
                v-model="selectedQualification"
                @change="handleSelectedQualification"
              >
                <option value="not_contacted">Not contacted</option>
                <option value="qualifying">Qualifying</option>
                <option value="contacted">Contacted</option>
                <option value="responded">Responded</option>
                <option value="interviewing">Interviewing</option>
                <option value="no_longer_interested">No longer interested</option>
                <option value="not_in_consideration">Not in consideration</option>
              </select>
              <label for="qualification">Qualification</label>
            </div>

            <div class="form-group m-label-left mb-4">
              <textarea
                :id="`candidate-comment-textarea-${pipeline.pipeline_id}`"
                v-model="activity_comment"
                class="form-control"
                style="min-height: 3rem"
                rows="1"
                placeholder="Аctivity comment"
                :disabled="isSubmitted === false"
              ></textarea>
              <label for="candidate-comment-textarea">Comment</label>
            </div>

            <div class="form-group m-label-left mb-4">
              <select v-model="selectedSource" class="form-control" id="candidate-source">
                <option v-for="source in getSourceList" :key="source.id" :value="source">
                  {{ source.description }} ({{ source.source_name }})
                </option>
              </select>
              <label for="source">Source</label>
            </div>
            <!-- DOCUMENTS -->
            <div class="form-group">
              <ul class="list-group file-listing mt-3">
                <li
                  :key="file.id"
                  v-for="file in getCandidateById.documents"
                  class="list-group-item d-flex align-items-center small"
                >
                  <div class="custom-control custom-checkbox d-flex">
                    <input
                      :id="`candidate-edit-pipeline-${file.id}-${rand}`"
                      class="custom-control-input"
                      type="checkbox"
                      v-model="selectedDocuments"
                      @change="handleCheckboxChange"
                      :value="file"
                      :disabled="isLoading"
                      aria-label
                    />
                    <label
                      class="custom-control-label"
                      :for="`candidate-edit-pipeline-${file.id}-${rand}`"
                    >
                      {{ file.name }}
                    </label>
                  </div>

                  <span class="badge badge-primary badge-pill ml-auto">{{
                    `${(file.file_size ? file.file_size / 1024 / 1024 : 0).toFixed(2)} MB`
                  }}</span>
                </li>
              </ul>
            </div>
            <!-- END DOCUMENTS -->
          </div>

          <div class="modal-footer text-center justify-content-center">
            <button @click="onUpdatePipeline" type="button" class="btn btn-primary">
              Save changes
            </button>
            <button type="button" class="btn btn-secondary" @click="onCancelEditCandidatePipeline">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- End Edit Modal -->

    <!-- Submit Modal -->
    <div
      class="modal fade"
      :id="`submitCandidateModal-${pipeline.pipeline_id}`"
      tabindex="-1"
      aria-labelledby="submitCandidateModal"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="submitCandidateModalLabel">Submit Candidate</h5>
            <button
              ref="closeSubmitModal"
              type="button"
              @click="clearForm"
              class="close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="d-flex align-items-center">
              <p>
                Please confirm that <b>{{ user.first_name }} {{ user.last_name }}</b> should be
                submitted for the position <b>{{ pipeline.job }}, {{ pipeline.employer }}</b>
              </p>
            </div>
            <textarea
              v-model="pipelineComment"
              class="form-control"
              rows="2"
              placeholder="Message to Employer"
              :disabled="isSending"
            ></textarea>
          </div>
          <div class="modal-footer text-center justify-content-center">
            <button
              :disabled="isSending"
              @click="onSubmitPipeline"
              type="button"
              class="btn btn-primary"
            >
              Confirm
            </button>
            <button
              :disabled="isSending"
              type="button"
              data-bs-dismiss="modal"
              class="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- End Submit Modal -->

    <!-- Delete Modal -->
    <div
      class="modal fade edit-modal"
      :id="`candidatePipelineDeleteModal-${pipeline.pipeline_id}`"
      tabindex="-1"
      aria-labelledby="candidatePipelineDeleteModal"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editModal2Label">Remove from Pipeline</h5>
            <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <p>
              Please confirm that you want to remove
              <b>{{ user.first_name }} {{ user.last_name }}</b> from the pipeline
              <b>{{ pipeline.job }}, {{ pipeline.employer }}.</b>
            </p>
          </div>
          <div class="modal-footer text-center justify-content-center">
            <button
              @click="onDeletePipeline(pipeline.pipeline_id)"
              type="button"
              class="btn btn-primary"
              data-bs-dismiss="modal"
            >
              Remove
            </button>

            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    <!-- End Delete Modal -->

    <!-- DevSkiller Modal -->
    <div
      class="modal fade"
      :id="`dev-${pipeline.pipeline_id}`"
      tabindex="-1"
      aria-labelledby="devModal"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="devModalLabel">Programming test</h5>
            <button
              ref="closeDevModal"
              type="button"
              class="close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="d-flex align-items-center">
              <p v-if="pipeline.has_assign_test === false">
                We are sorry, but no test has been assigned to this job yet.
              </p>

              <p
                v-if="
                  pipeline.devskiller_data.status === 'Completed' ||
                    pipeline.devskiller_data.status === 'Sent' ||
                    pipeline.devskiller_data.status === 'Expired' ||
                    pipeline.devskiller_data.status === 'Shared' ||
                    pipeline.devskiller_data.status === 'Declined'
                "
              >
                <strong>{{ user.first_name }} {{ user.last_name }}</strong> has already received an
                invitation for a programming test. The invite was sent on<strong>
                  {{
                    pipeline.devskiller_data.created_date
                      | formatDate({
                        format: getUserProfile.format_date,
                        timezone: getUserProfile.timezone,
                        hasHourAndMinutes: false,
                      })
                  }}</strong
                >
                by <strong>{{ pipeline.devskiller_data.creator }}</strong
                >.
              </p>
              <p v-if="pipeline.devskiller_data.length === 0 && pipeline.has_assign_test === true">
                Please confirm that you want to invite
                <strong>{{ user.first_name }} {{ user.last_name }}</strong> to conduct the assigned
                online programming test. You will be notified when the test has been completed. The
                candidate will have 10 days to complete the test. <br />
                <br />
                In general, we recommend to have an interview with the candidate before inviting
                him/her to do the programming test.
              </p>
            </div>
          </div>
          <div
            v-if="pipeline.has_assign_test === false"
            class="modal-footer text-center justify-content-center"
          >
            <button
              data-bs-dismiss="modal"
              aria-label="Close"
              type="button"
              class="btn btn-primary"
            >
              OK
            </button>
            <!--   <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Contact Jobshark
            </button> -->
          </div>
          <div
            v-if="pipeline.devskiller_data.length === 0 && pipeline.has_assign_test === true"
            class="modal-footer text-center justify-content-center"
          >
            <button
              @click="sendTest"
              data-bs-dismiss="modal"
              aria-label="Close"
              type="button"
              class="btn btn-primary"
            >
              Send invite to test
            </button>
            <button
              :disabled="isSending"
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
          </div>
          <div
            v-if="
              pipeline.devskiller_data.status === 'Completed' ||
                pipeline.devskiller_data.status === 'Sent' ||
                pipeline.devskiller_data.status === 'Expired' ||
                pipeline.devskiller_data.status === 'Shared' ||
                pipeline.devskiller_data.status === 'Declined'
            "
            class="modal-footer text-center justify-content-center"
          >
            <button
              @click="sendTest"
              data-bs-dismiss="modal"
              aria-label="Close"
              type="button"
              class="btn btn-primary"
            >
              Send new invite
            </button>
            <button
              :disabled="isSending"
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <TimeBookingModal :pipeline="pipeline" @update="updatePipeline" />
    <ConfirmBookingModal :pipeline="pipeline" @update="updatePipeline" />
  </tr>
</template>

<script>
import { stagesMixin } from '../../../mixins/stagesMixin';
import { qualificationMixin } from '../../../mixins/qualificationMixin';
import TimeBookingModal from '../common/TimeBookingModal.vue';
import ConfirmBookingModal from '../common/ConfirmBookingModal.vue';
import { errorService } from '../../../services/errorService';
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import axios from 'axios';

export default {
  mixins: [stagesMixin, qualificationMixin],
  props: {
    pipeline: {
      type: Object,
    },
    user: {
      type: Object,
    },
  },
  components: {
    TimeBookingModal,
    ConfirmBookingModal,
  },
  data: () => ({
    rand: Math.floor(1000 + Math.random() * 9000),
    isSending: false,
    pipelineComment: '',
    starPoints: [23, 2, 14, 17, 0, 19, 10, 34, 7, 50, 23, 43, 38, 50, 36, 34, 46, 19, 31, 17],
    isSubmitted: false,
    isSeen: true,
    selectedStage: { value: 'not_contacted' },
    selectedQualification: {},
    candidateDocumentsFiles: [],
    selectedSource: {},
    activity_comment: '',
    currentStage: '',
    currentQualification: '',
    currentSource: {},
    currentUpdatedAt: '',
    isLoading: false,
    selectedDocuments: [],
    pipelineDocuments: [],
    isSubmitDisabled: false,
    isDisabled: false,
  }),
  methods: {
    ...mapActions([
      'retrieveSourcesList',
      'submitCandidatePipeline',
      'retrieveDocumentPreview',
      'setCandidatePipelineRating',
      'updatePipelineForCandidate',
      'retrieveCandidateDocument',
      'removeCandidateFromPipeline',
    ]),

    checkInterviewStatus() {
      if (this.pipeline.interview.length === 0) {
        return 'Book interview';
      }
      if (this.pipeline.interview.status === 1) {
        return 'Meeting requested';
      }

      if (this.pipeline.interview.status === 2) {
        return 'Cancel interview';
      }
    },

    updatePipeline() {
      this.$emit('pipeline-update', { currentPage: this.getCurrentPage, count: 1000 });
    },

    sendTest() {
      axios
        .post(
          `${process.env.VUE_APP_API_URL}/admin/devskiller/sendinvitation`,
          {
            pipeline_id: this.pipeline.pipeline_id,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                `${process.env.VUE_APP_TOKEN_PREFIX}token`,
              )}`,
            },
          },
        )
        .then(res => {
          this.$toasted.success(
            `An invite to the programming test has been sent to ${this.user.first_name} ${this.user.last_name}`,
            {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            },
          );
          this.$refs.closeDevModal.click();
          this.$emit('pipeline-update', { currentPage: this.getCurrentPage, count: 1000 });
        })
        .catch(err => {
          console.log(err);
        });
    },
    getStatusOfQualification() {
      if (this.getQualificationMixin(this.pipeline.qualification).value === 'submitted') {
        this.isDisabled = true;
        return false;
      }

      this.isDisabled = false;
    },
    setCurrentPipeline() {
      if (this.currentQualification !== 'Submitted') {
        this.currentQualification = this.getQualificationMixin(this.selectedQualification).title;
      }

      if (this.currentQualification === 'Submitted') {
        this.currentStage = this.getStageMixin(this.selectedStage).title;

        /* Set Modal Values */
        this.selectedStage = this.getStageMixin(this.selectedStage).value;
      }

      this.currentUpdatedAt = this.pipeline.updated_at;
      this.currentSource = this.selectedSource;
      this.pipelineDocuments = [...this.selectedDocuments];
    },
    handleCheckboxChange() {
      // console.log(this.selectedDocuments);
    },
    setFromAddCandidate(from) {
      if (from === 'add-candidate') {
        this.currentQualification = this.getQualificationMixin(this.pipeline.qualification).title;
        this.currentStage = this.getStageMixin(this.pipeline.stage).title;
      }
    },
    setDefaultDocument() {
      this.selectedDocuments = this.pipelineDocuments;
    },
    openDocPreview(docId, name) {
      const fileExtension = name
        .split('.')
        .pop()
        .toLowerCase();
      const imageFileTypes = ['jpg', 'jpeg', 'png', 'heic'];
      if (imageFileTypes.includes(fileExtension)) {
        this.retrieveDocumentPreview({ uuid: docId, preview: 'true' })
          .then(response => {
            let mimeType;
            switch (fileExtension) {
              case 'png':
                mimeType = 'image/png';
                break;
              case 'heic':
                mimeType = 'image/heic';
                break;
              default:
                mimeType = 'image/jpeg';
            }

            const blob = new window.Blob([response.data], { type: mimeType });
            const fileURL = window.URL.createObjectURL(blob);

            // Open the image in a new tab
            const newWindow = window.open('', '_blank');
            newWindow.document.write(`
          <html>
            <head>
              <title>Image Preview</title>
            </head>
            <body style="margin:0;">
              <img src="${fileURL}" style="width:100%; height:100%; object-fit:contain;" />
            </body>
          </html>
        `);
            newWindow.document.close();
          })
          .catch(() => {
            this.isCvLoading = false;
          });
      } else {
        this.isCvLoading = true;
        this.retrieveDocumentPreview({ uuid: docId, preview: 'true' })
          .then(response => {
            this.isCvLoading = false;
            const blob = new window.Blob([response.data], { type: 'application/pdf' });

            const fileURL = window.URL.createObjectURL(blob);

            window.open(fileURL, '_blank').focus();
          })
          .catch(() => {
            this.isCvLoading = false;
          });
      }
    },
    onDownloadDocument(docId, docTitle) {
      this.retrieveCandidateDocument({ uuid: docId, preview: 'false' })
        .then(response => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute('download', `${docTitle}`);
          document.body.appendChild(fileLink);

          fileLink.click();
        })
        .catch(err => {
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    getDocumentProperties(fileName) {
      const re = /(?:\.([^.]+))?$/;

      if (re.exec(fileName)[1] === 'pdf') {
        return {
          color: '#ff0000',
          type: 'pdf',
        };
      } else if (re.exec(fileName)[1] === 'docx' || re.exec(fileName)[1] === 'doc') {
        return {
          color: '#005a9e',
          type: 'doc',
        };
      } else {
        return { color: '#a7a7a77', type: '' };
      }
    },
    handleSelectedQualification() {
      this.isSubmitted = true;
    },
    handleSelectedStage() {
      this.isSubmitted = true;
    },
    onUpdatePipeline() {
      this.isLoading = true;

      this.updatePipelineForCandidate({
        pipeline_id: this.pipeline.pipeline_id,
        stage: this.selectedStage === 'offered_employment' ? 'offered' : this.selectedStage,
        qualification: this.selectedQualification,
        source_id: this.selectedSource.id,
        documents: this.selectedDocuments,
        activity_comment: this.activity_comment,
      })
        .then(() => {
          this.isLoading = false;

          this.$toasted.success('Pipeline updated', {
            theme: 'bubble',
            icon: 'fa-check-circle',
            position: 'bottom-right',
            duration: 5000,
          });

          this.setCurrentPipeline();

          // Set new date and time
          this.currentUpdatedAt = moment().format();

          // Update activity

          this.$emit('pipeline-update', { currentPage: this.getCurrentPage, count: 1000 });
          this.$refs.closeEditModal.click();
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 5000,
          });
        });
    },
    onSubmitPipeline() {
      this.isSending = true;
      this.submitCandidatePipeline({
        pipeline_id: this.pipeline.pipeline_id,
        submitted_comment: this.pipelineComment,
      })
        .then(() => {
          this.isLoading = false;
          this.isSending = false;
          this.currentQualification = 'Submitted';
          this.currentStage = 'Not contacted';
          this.currentUpdatedAt = moment().format();
          this.isDisabled = true;

          this.selectedStage = 'not_contacted';

          this.$toasted.show('Pipeline submitted', {
            theme: 'bubble',
            icon: 'fa-check-circle',
            position: 'bottom-right',
            duration: 5000,
          });

          // Update activity

          this.$emit('pipeline-update', { currentPage: this.getCurrentPage, count: 1000 });

          this.$refs.closeSubmitModal.click();
        })
        .catch(err => {
          this.isLoading = false;
          this.isSending = false;
          if (err.response.data === 'Please add address before submit a candidate') {
            this.$toasted.error(
              'Candidate could not be submitted. A candidate has to have a registered location and a valid email address to be submitted.',
              {
                theme: 'bubble',
                icon: 'fa-times-circle',
                position: 'bottom-right',
                duration: 5000,
              },
            );
          }
        });
    },
    onCancelEditCandidatePipeline() {
      this.selectedSource = this.pipeline.source;
      this.selectedQualification = this.getQualificationMixin(this.pipeline.qualification).value;
      this.selectedStage = this.getStageMixin(this.pipeline.stage).value;
      this.selectedSource = this.pipeline.source;
      this.activity_comment = '';
      this.isSubmitted = false;

      this.$refs.closeEditModal.click();
    },
    clearForm() {
      this.isSubmitted = false;
    },
    onDeletePipeline(pipelineID) {
      this.isLoading = true;

      this.removeCandidateFromPipeline({ pipeline_id: pipelineID })
        .then(() => {
          this.isLoading = false;

          this.$emit('pipeline-reload', { currentPage: this.getCurrentPage, count: 1000 });
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 5000,
          });
        });
    },
    setCurrentSelectedRating(rating, pipelineId) {
      this.setCandidatePipelineRating({ rating, pipeline_id: pipelineId })
        .then(() => {})
        .catch(err => {
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 5000,
          });
        });
    },
  },
  watch: {
    pipeline: {
      handler(newVal, oldVal) {
        // Kick's when user add candidate from AddCandidateBtn
        if (oldVal !== '') {
          this.currentQualification = this.getQualificationMixin(newVal.qualification).title;
          this.currentSource = newVal.source;
          this.currentUpdatedAt = newVal.updated_at;

          /* SET MODAL VALUES */
          this.selectedQualification = this.getQualificationMixin(newVal.qualification).value;
          this.selectedSource = newVal.source;
          this.selectedStage = this.getStageMixin(newVal.stage).value;
        }
        if (this.selectedQualification) {
          this.getStatusOfQualification();
        }

        this.pipelineDocuments = [...newVal.documents];
        this.currentStage = this.getStageMixin(this.pipeline.stage).title;
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(['getCandidateById', 'getSourceList', 'getUserProfile', 'getCurrentPage']),
  },
  mounted() {
    this.setCurrentPipeline();
    // this.retrieveSourcesList();

    this.pipelineDocuments = [...this.pipeline.documents];

    // SET MODAL DEFAULT VALUES
    this.selectedSource = this.pipeline.source;
    this.selectedQualification = this.getQualificationMixin(this.pipeline.qualification).value;
    this.selectedStage = this.getStageMixin(this.pipeline.stage).value;
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.icon-wrapper {
  .svg-icon {
    width: 18px;
    height: 18px;
  }

  .edit {
    fill: #a7a7a7;
  }

  .submit {
    fill: #5bb75b;
  }
}

#stage {
  .stage-size {
    width: 12px;
    height: 12px;
  }
}

.icon-wrapper {
  .send-test {
    .svg-icon {
      margin-top: 4px;
      width: 25px;
      height: 25px;
    }
  }
  svg {
    width: 16px;
    height: 18px;
  }
}

.truncate-item {
  a {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }
}

.location-color {
  color: #444;
}

.edit {
  fill: #a7a7a7;
}

.star-rating {
  position: relative;
  display: block;
  width: 100%;
  .reset-stars {
    position: absolute;
    font-size: 16.7px;
    cursor: pointer;
    top: 1px;
    right: -18px;
    color: #555;
    display: none;
  }
  &:hover {
    .reset-stars {
      display: block;
      right: 10px;
    }
  }
}
</style>
